// FILE: src/Menu.js  (full replacement)
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// Typography/colour to match the About heading ("About Quizmaster")
const FONT_FAMILY = "'Montserrat', 'Open Sans', Arial, sans-serif";
const HEADING_COLOR = "#e2eefd";
const HEADING_SHADOW = "2px 2px 14px #2ae, 0 2px 12px #09f, 0 4px 16px #222c";

export default function Menu({ open, onClose, onReconnect }) {
  const [role, setRole] = useState("");
  const [userEmail, setUserEmail] = useState(""); // <<< FIX: Declare userEmail state
  const navigate = useNavigate();

  // Close with ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Load role from Firestore (users/{uid}.role)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setRole("");
        setUserEmail(""); // <<< FIX: Clear email on logout
        return;
      }
      setUserEmail(u.email); // <<< FIX: Set user's email on login
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        setRole((snap.exists() && snap.data()?.role) || "");
      } catch {
        setRole("");
      }
    });
    return () => unsub();
  }, []);

  // SPA-safe navigation helper
  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  const doLogout = async () => {
    try {
      await signOut(auth);
    } catch {}
    onClose?.();
    navigate("/host");
  };

  // Hardcoded Qwizard check using both role from DB and email
  const isQwizard =
    role === "Qwizard" ||
    userEmail === 'rob@izeus.org' ||
    userEmail === 'suelaw42@gmail.com';

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: open ? "rgba(0,0,0,0.45)" : "transparent",
          backdropFilter: open ? "blur(1.5px)" : "none",
          transition: "background 160ms ease",
          pointerEvents: open ? "auto" : "none",
          zIndex: 2000,
        }}
      />
      {/* Slide-in panel */}
      <aside
        role="navigation"
        aria-label="Main menu"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: 300,
          maxWidth: "86vw",
          background: "rgba(32,30,48,0.92)",
          color: HEADING_COLOR,
          borderRight: "1px solid rgba(255,255,255,0.14)",
          boxShadow: "8px 0 32px rgba(0,0,0,0.45)",
          transform: open ? "translateX(0)" : "translateX(-105%)",
          transition: "transform 220ms ease",
          zIndex: 2001,
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
        }}
      >
        <div
          style={{
            padding: "18px 16px 12px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <div
            style={{
              fontSize: "1.35rem",
              fontWeight: 900,
              letterSpacing: 0.6,
              color: HEADING_COLOR,
              textShadow: HEADING_SHADOW,
            }}
          >
            Quizmaster Bob
          </div>
          <div
            style={{
              fontSize: "0.95rem",
              fontWeight: 800,
              marginTop: 4,
              color: HEADING_COLOR,
              textShadow: HEADING_SHADOW,
              opacity: 0.9,
            }}
          >
            Navigation
          </div>
        </div>

        <nav
          style={{
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            overflowY: "auto",
          }}
        >
          <MenuBtn label="Home" onClick={() => go("/host")} />

          {/* Admin (existing) */}
          {isQwizard && (
            <MenuBtn label="Admin" onClick={() => go("/quizaccess")} />
          )}

          {/* NEW: Library Viewer (Qwizard only) */}
          {isQwizard && (
            <MenuBtn
              label="Library Viewer"
              onClick={() => go("/library-viewer")}
            />
          )}

          {isQwizard && (
            <MenuBtn
              label="Music Downloader"
              onClick={() => go("/music-downloader")}
            />
          )}

          {/* NEW: Find Duplicates (Qwizard only) */}
          {isQwizard && (
            <MenuBtn
              label="Find Duplicates"
              onClick={() => go("/duplicate-questions")}
            />
          )}

          {/* NEW: Bugs/Features/Releases (Qwizard only) */}
          {isQwizard && (
            <MenuBtn
              label="Bugs/Features/Releases"
              onClick={() => go("/bugs-features-releases")}
            />
          )}

          {/* NEW: Marketing (Qwizard only) */}
          {isQwizard && (
            <MenuBtn
              label="Marketing"
              onClick={() => go("/marketing")}
            />
          )}

          {/* NEW: Test Runner (Qwizard + Localhost only) */}
          {isQwizard && (window.location.hostname === 'localhost' || 
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname === '::1') && (
            <MenuBtn
              label="Test Runner"
              onClick={() => go("/testQuizscript")}
            />
          )}

          {/* Removed "Find Quiz" as requested previously */}
          <MenuBtn label="About" onClick={() => go("/About")} />
          <MenuBtn label="Pricing" onClick={() => go("/pricing")} />
          <MenuBtn label="Account" onClick={() => go("/account")} />
          {typeof onReconnect === "function" && (
            <MenuBtn
              label="Reconnect"
              onClick={() => {
                onReconnect();
                onClose?.();
              }}
            />
          )}
          <div style={{ height: 8 }} />
          <MenuBtn label="Log out" onClick={doLogout} strong />
        </nav>

        <div
          style={{
            marginTop: "auto",
            padding: "10px 14px",
            fontSize: "0.82rem",
            opacity: 0.7,
            color: HEADING_COLOR,
          }}
        >
          Press Esc to close
        </div>
      </aside>
    </>
  );
}

function MenuBtn({ label, onClick, strong = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "12px 14px",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.14)",
        borderRadius: 12,
        color: HEADING_COLOR,
        fontWeight: strong ? 900 : 700,
        letterSpacing: 0.3,
        cursor: "pointer",
        transition: "background 120ms ease, transform 80ms ease",
        fontFamily: FONT_FAMILY,
        textShadow: HEADING_SHADOW,
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.99)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      {label}
    </button>
  );
}
